import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

interface ActiveCompanyContextValue {
  /** The company shown in the top bar AND used to fetch that company's process configuration for
   * sidebar gating (see AppShell). For an ordinary company user these stay unused (their own
   * company/config comes from UserDto.companyId + the self-service endpoint instead); for a
   * company-less SuperAdmin, a page scoped to one specific company (e.g.
   * CompanyProcessConfigurationPage) announces it here so both the header and the nav reflect
   * whichever tenant they're currently acting on, instead of the self-service call 400ing for a
   * user with no company of their own. */
  activeCompanyId: string | null;
  activeCompanyName: string | null;
  setActiveCompany: (company: { id: string; name: string } | null) => void;
}

const ActiveCompanyContext = createContext<ActiveCompanyContextValue | undefined>(undefined);

export function ActiveCompanyProvider({ children }: { children: ReactNode }) {
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [activeCompanyName, setActiveCompanyName] = useState<string | null>(null);
  const setActiveCompany = useCallback((company: { id: string; name: string } | null) => {
    setActiveCompanyId(company?.id ?? null);
    setActiveCompanyName(company?.name ?? null);
  }, []);
  const value = useMemo(
    () => ({ activeCompanyId, activeCompanyName, setActiveCompany }),
    [activeCompanyId, activeCompanyName, setActiveCompany],
  );
  return <ActiveCompanyContext.Provider value={value}>{children}</ActiveCompanyContext.Provider>;
}

export function useActiveCompany(): ActiveCompanyContextValue {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) throw new Error('useActiveCompany must be used within an ActiveCompanyProvider');
  return ctx;
}
